import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HiOutlineCheckCircle, HiOutlinePrinter, HiOutlineHome, HiOutlineTicket } from "react-icons/hi2";
import { FaCalendarAlt, FaMapMarkerAlt, FaUserFriends } from "react-icons/fa";

const BookingSuccess = () => {
  const { state } = useLocation();
  const navigate = useNavigate();

  // The 'booking' object comes from the navigate(state) in HotelCommit.js
  const bookingData = state?.booking;
  
  // Extract details from the response_payload stored in the DB
  const tboDetails = bookingData?.response_payload?.CommitBooking?.BookingDetails;

  useEffect(() => {
    if (!state || !bookingData) {
      navigate("/");
    }
  }, [state, bookingData, navigate]);

  if (!bookingData) return null;

  return (
    <section className="bg-light min-vh-100 py-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            {/* Success Header */}
            <div className="text-center mb-5">
              <HiOutlineCheckCircle className="text-success display-1 mb-3" />
              <h2 className="fw-bold">Booking Confirmed!</h2>
              <p className="text-muted">
                Your reservation at <strong>{tboDetails?.HotelName || "the hotel"}</strong> is complete. 
                A confirmation email has been sent.
              </p>
            </div>

            {/* Main Ticket Card */}
            <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4">
              <div className="bg-primary p-4 text-white d-flex justify-content-between align-items-center">
                <div>
                  <small className="opacity-75 text-uppercase fw-bold">Booking Reference</small>
                  <h4 className="mb-0 fw-bold">{bookingData.booking_reference}</h4>
                </div>
                <div className="text-end">
                  <span className="badge bg-white text-primary px-3 py-2 rounded-pill">
                    {tboDetails?.booking_status || "CONFIRMED"}
                  </span>
                </div>
              </div>

              <div className="card-body p-4">
                <div className="row g-4">
                  {/* Left Column: Hotel Details */}
                  <div className="col-md-7">
                    <h5 className="fw-bold mb-3">Reservation Details</h5>
                    <div className="d-flex mb-3">
                      <FaCalendarAlt className="text-primary me-3 mt-1" />
                      <div>
                        <small className="text-muted d-block">Check-in — Check-out</small>
                        <span className="fw-medium">
                          {tboDetails?.CheckInDate} — {tboDetails?.CheckOutDate}
                        </span>
                      </div>
                    </div>
                    <div className="d-flex mb-3">
                      <FaMapMarkerAlt className="text-primary me-3 mt-1" />
                      <div>
                        <small className="text-muted d-block">Address</small>
                        <span className="fw-medium">{tboDetails?.HotelAddress || "Address details in voucher"}</span>
                      </div>
                    </div>
                    <div className="d-flex mb-0">
                      <FaUserFriends className="text-primary me-3 mt-1" />
                      <div>
                        <small className="text-muted d-block">Room Type</small>
                        <span className="fw-medium">{tboDetails?.RoomTypeName || "Standard Room"}</span>
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Guest & Payment Summary */}
                  <div className="col-md-5 border-start ps-md-4">
                    <h5 className="fw-bold mb-3">Payment Summary</h5>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Status</span>
                      <span className="text-success fw-bold">Paid</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Total Paid</span>
                      <span className="fw-bold text-dark">₹{bookingData.amount}</span>
                    </div>
                    <hr />
                    <div className="bg-light p-3 rounded-3 mt-3">
                      <h6 className="fw-bold small mb-2"><HiOutlineTicket className="me-2"/>Voucher Info</h6>
                      <small className="text-muted d-block">Confirmation No:</small>
                      <span className="fw-bold text-primary">{tboDetails?.ConfirmationNo || "Processing..."}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="d-flex gap-3 justify-content-center">
              <button 
                onClick={() => window.print()} 
                className="btn btn-outline-primary rounded-pill px-4 py-2 d-flex align-items-center"
              >
                <HiOutlinePrinter className="me-2" /> Print Voucher
              </button>
              <button 
                onClick={() => navigate("/")} 
                className="btn btn-primary rounded-pill px-4 py-2 d-flex align-items-center"
              >
                <HiOutlineHome className="me-2" /> Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingSuccess;